import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Autocomplete, Box, Button, DialogActions, DialogContent, DialogTitle, Drawer, FormControl, FormLabel, Grid, IconButton, Input, ModalClose, Select, Option } from "@mui/joy";
import React from "react";

export interface BuildingKeyRecord {
	building_keys_id: string;
	buildings_id: string;
	key_value: string;
	key_type: string;
	elevator_serial_number: string;
}

interface BuildingKeysDialogProps {
	buildingId: string;
	password: string;
	keyRecord?: BuildingKeyRecord;
	close: () => void;
	onSaved?: () => void;
}

const KEY_TYPES = ["HMI Password", "Controller Password", "PLC Password"];
const KEY_CHARSET = "ABCDEFGHIJKLMNPQRSTUVWXYZ123456789";

export function generateBuildingKey(): string {
	let key = "";
	for (let i = 0; i < 8; i++) {
		key += KEY_CHARSET[Math.floor(Math.random() * KEY_CHARSET.length)];
	}
	return key;
}

export default function BuildingKeysDialog(props: BuildingKeysDialogProps) {
	const isEdit = !!props.keyRecord?.building_keys_id;
	const [open, setOpen] = React.useState(true);
	const [keyValue, setKeyValue] = React.useState(props.keyRecord?.key_value ?? generateBuildingKey);
	const [keyType, setKeyType] = React.useState(props.keyRecord?.key_type ?? "");
	const [elevatorSerial, setElevatorSerial] = React.useState(props.keyRecord?.elevator_serial_number ?? "");
	const [serialOptions, setSerialOptions] = React.useState([] as string[]);
	const [error, setError] = React.useState("");
	const [saving, setSaving] = React.useState(false);

	React.useEffect(() => {
		fetch("Building/GetElevators", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ buildingsId: props.buildingId }),
		})
			.then((x) => x.json())
			.then((x) => {
				const serials = (x as Array<{ serial_number?: string }>)
					.map((e) => e.serial_number?.trim())
					.filter((s): s is string => !!s);
				setSerialOptions(Array.from(new Set(serials)));
			});
	}, [props.buildingId]);

	const close = () => {
		setOpen(false);
		props.close();
	};

	const save = (overrideKeyValue?: string) => {
		const valueToSave = overrideKeyValue ?? keyValue;

		if (!keyType) {
			setError("Key Type is required.");
			return Promise.resolve(false);
		}

		setError("");
		setSaving(true);
		return fetch("Building/SaveBuildingKey", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				password: props.password,
				keyData: {
					building_keys_id: props.keyRecord?.building_keys_id ?? null,
					buildings_id: props.buildingId,
					key_value: valueToSave,
					key_type: keyType,
					elevator_serial_number: elevatorSerial.trim() || null,
				},
			}),
		})
			.then((x) => x.json())
			.then((x) => {
				if (x?.error) {
					setError(x.error);
					return false;
				}
				if (overrideKeyValue) {
					setKeyValue(overrideKeyValue);
					props.onSaved?.();
				} else {
					close();
				}
				return true;
			})
			.finally(() => setSaving(false));
	};

	const regenerate = () => {
		const newKey = generateBuildingKey();
		if (isEdit) {
			save(newKey);
		} else {
			setKeyValue(newKey);
		}
	};

	const deleteKey = () => {
		if (!props.keyRecord?.building_keys_id) return;
		if (!window.confirm("Are you sure you want to delete this key? This cannot be undone.")) return;

		setError("");
		setSaving(true);
		fetch("Building/DeleteBuildingKey", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				password: props.password,
				buildingKeysId: props.keyRecord.building_keys_id,
			}),
		})
			.then((x) => x.json())
			.then((x) => {
				if (x?.error) {
					setError(x.error);
					return;
				}
				close();
			})
			.finally(() => setSaving(false));
	};

	return (
		<Drawer anchor="right" open={open} size="md">
			<ModalClose onClick={close} />
			<DialogTitle>{isEdit ? "Edit Building Key" : "Add Building Key"}</DialogTitle>
			<DialogContent sx={{ padding: 2 }}>
				<Grid container spacing={2}>
					<Grid xs={12}>
						<FormControl>
							<FormLabel>Key</FormLabel>
							<Input
								value={keyValue}
								disabled
								endDecorator={
									<Box sx={{ display: "flex", gap: 0.5 }}>
										<IconButton variant="plain" size="sm" title="Regenerate key" disabled={saving} onClick={regenerate}>
											<RefreshIcon fontSize="small" />
										</IconButton>
										<IconButton variant="plain" size="sm" title="Copy key" onClick={() => navigator.clipboard.writeText(keyValue)}>
											<ContentCopyIcon fontSize="small" />
										</IconButton>
									</Box>
								}
							/>
						</FormControl>
					</Grid>
					<Grid xs={12}>
						<FormControl required>
							<FormLabel>Key Type</FormLabel>
							<Select value={keyType} onChange={(e, value) => setKeyType(value ?? "")} placeholder="Select key type">
								{KEY_TYPES.map((type) => (
									<Option key={type} value={type}>
										{type}
									</Option>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid xs={12}>
						<FormControl>
							<FormLabel>Elevator Serial Number</FormLabel>
							<Autocomplete
								freeSolo
								options={serialOptions}
								value={elevatorSerial}
								onInputChange={(event, value) => setElevatorSerial(value)}
								placeholder="Optional"
							/>
						</FormControl>
					</Grid>
					{error && (
						<Grid xs={12}>
							<Box sx={{ color: "danger.plainColor" }}>{error}</Box>
						</Grid>
					)}
				</Grid>
			</DialogContent>
			<DialogActions>
				<Box sx={{ padding: 2 }}>
					{!isEdit && (
						<Button size="lg" color="primary" loading={saving} onClick={() => save()} sx={{ mx: 1 }}>
							Save
						</Button>
					)}
					{isEdit && (
						<Button size="lg" color="danger" loading={saving} onClick={deleteKey} sx={{ mx: 1 }}>
							Delete
						</Button>
					)}
					<Button size="lg" color="neutral" onClick={close} sx={{ mx: 1 }}>
						{isEdit ? "Close" : "Cancel"}
					</Button>
				</Box>
			</DialogActions>
		</Drawer>
	);
}
